import React from "react";
import {
  NavigateToFunctions,
  processProjectParams,
} from "../../../../routes/RoutesHelper";
import { UploadFileDialog } from "../../../../components/fileUpload/UploadFileDialog";
import {
  FoundationColorTokens,
  Spacing,
  Typography,
} from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import { RespondentSPoCUploadPane } from "./RespondentSPoCUploadPane";
import { NetworkingError } from "@pulse/shared-components";
import { TFunction } from "i18next";
import { Instance } from "mobx-state-tree";
import { RespondentSPoCMappingStore } from "../../store/RespondentSPoCMappingStore";
import { ProjectDetailsStore } from "../../store/ProjectDetailsStore";

export interface RespondentSPoCFileUploadDialogProps {
  t: TFunction;
  spacing: Spacing;
  tokens: FoundationColorTokens<string>;
  typography: Typography;
  navigateTo: NavigateToFunctions;
  respondentSPoCMappingStore: Instance<
    typeof RespondentSPoCMappingStore
  > | null;
  projectStore: Instance<typeof ProjectDetailsStore>;
}

export const RespondentSPoCFileUploadComponent = observer(
  ({
    t,
    spacing,
    tokens,
    typography,
    navigateTo,
    respondentSPoCMappingStore,
    projectStore,
  }: RespondentSPoCFileUploadDialogProps): React.ReactElement => {
    if (respondentSPoCMappingStore?.rpcErrors) {
      switch (respondentSPoCMappingStore.rpcErrors) {
        case NetworkingError.InternalError:
        case NetworkingError.InternalServerError: {
          navigateTo.internalServerError();
        }
      }
    }

    const getErrorBannerText = (): string | undefined => {
      if (respondentSPoCMappingStore?.isFileInvalid) {
        return t("common.uploadDialog.invalidFileDetailsBannerText");
      } else {
        return undefined;
      }
    };

    const downloadButtonOnClick = (): Promise<void> => {
      return processProjectParams(async () => {
        await respondentSPoCMappingStore?.downloadRespondentSpocMappingTemplate();
      }, navigateTo);
    };

    const dialogButtonOnClick = async (): Promise<void> => {
      respondentSPoCMappingStore?.setIsRespondentSPoCMappingUploadDialogVisible(
        false,
      );
      if (projectStore.respondentStore.projectId !== null) {
        await respondentSPoCMappingStore?.setIsRespondentSPoCAssignmentValidationDialogVisible(
          true,
        );
        await respondentSPoCMappingStore?.validateAndUpdateRespondentSPoCMapping(
          projectStore.respondentStore.projectId,
        );
      } else {
        console.error(
          "Project id was found to be null in the respondent store.",
        );
      }
      return Promise.resolve();
    };

    return respondentSPoCMappingStore ? (
      <UploadFileDialog
        errorBannerText={getErrorBannerText()}
        isUploadFileDialogVisible={
          respondentSPoCMappingStore?.isRespondentSPoCMappingUploadDialogVisible
        }
        title={t("common.uploadSPoCCSV")}
        description={t("projects.respondentList.downloadTemplateDescription")}
        note={t("common.uploadDialog.uploadNoteText")}
        primaryDownloadButtonText={t("common.downloadTemplate")}
        primaryDownloadButtonOnClick={downloadButtonOnClick}
        primaryDialogButtonText={t("common.proceed")}
        primaryDialogButtonOnClick={dialogButtonOnClick}
        secondaryDialogButtonText={t("common.cancel")}
        isPrimaryDialogButtonDisabled={
          !respondentSPoCMappingStore?.hasFileUploaded ||
          respondentSPoCMappingStore.doesStoreContainErrors
        }
        fileUploadPane={
          <RespondentSPoCUploadPane
            respondentSPoCMappingStore={respondentSPoCMappingStore}
            navigateTo={navigateTo}
            t={t}
            spacing={spacing}
            tokens={tokens}
            typography={typography}
          />
        }
        isSecondaryDialogButtonDisabled={
          respondentSPoCMappingStore.isRPCLoading
        }
        secondaryDialogButtonOnClick={() => {
          respondentSPoCMappingStore.resetRPCErrors();
          respondentSPoCMappingStore.resetValidationErrors();
          respondentSPoCMappingStore.resetSPoCMappingCSVStates();
          respondentSPoCMappingStore?.setIsRespondentSPoCMappingUploadDialogVisible(
            false,
          );
        }}
      />
    ) : (
      <></>
    );
  },
);
